import { PortalProfile, Profile } from './reducers'

import { getCountryCodeByCurrentDomain } from '~/util/domain/getCountryCodeByCurrentDomain'
import {
  queryDefaultShipper,
  updateAddressBookEntry,
  createAddressBookEntry,
} from '~/address-book/model/AddressService'
import { AddressBookEntry } from '~/address-book/model/reducer'
import { get } from '~/util/api/get'
import { getConfig } from '~/config'

const fetchPortalShipperAddress = async () => {
  const portalProfile = await get<PortalProfile>(`/receiver-profile-api/profile`)
  const shipperAddress = portalProfile.addresses.find((address) => address.isDefaultShippingAddress)
  return {
    isDefined: !!shipperAddress,
    id: (shipperAddress && shipperAddress.addressId) || null,
    firstName: (shipperAddress && shipperAddress.addressDetails.firstName) || '',
    lastName: (shipperAddress && shipperAddress.addressDetails.lastName) || '',
    companyName: (shipperAddress && shipperAddress.addressDetails.companyName) || '',
    address: {
      countryCode: (shipperAddress && shipperAddress.address.countryCode) || '',
      postalCode: (shipperAddress && shipperAddress.address.postalCode) || '',
      city: (shipperAddress && shipperAddress.address.city) || '',
      street: (shipperAddress && shipperAddress.address.street) || '',
      houseNumber: (shipperAddress && shipperAddress.address.houseNumber) || '',
      houseNumberSuffix: (shipperAddress && shipperAddress.address.houseNumberSuffix) || '',
      suburb: (shipperAddress && shipperAddress.address.suburb) || '',
      additionalAddressLine: (shipperAddress && shipperAddress.address.lines?.map((line) => line)) || '',
    },
    email: (shipperAddress && shipperAddress.addressDetails.email) || '',
    phoneNumber: (shipperAddress && shipperAddress.addressDetails.mobileNumber) || '',
    vatNumber: (shipperAddress && shipperAddress.addressDetails.vatNumber) || '',
  }
}

const fetchPwaShipperAddress = async () => {
  const defaultShipper = await queryDefaultShipper()

  const vatIndex = (defaultShipper && defaultShipper.tags.findIndex((tag) => tag.slice(0, 4) === 'VAT-')) || -1
  const vatNumber = vatIndex !== -1 ? defaultShipper.tags[vatIndex].slice(4, defaultShipper.tags[vatIndex].length) : ''
  return {
    isDefined: !!defaultShipper,
    id: (defaultShipper && defaultShipper.id) || null,
    firstName: (defaultShipper && defaultShipper.name.firstName) || '',
    lastName: (defaultShipper && defaultShipper.name.lastName) || '',
    companyName: (defaultShipper && defaultShipper.name.companyName) || '',
    address: {
      countryCode: (defaultShipper && defaultShipper.address.countryCode) || '',
      postalCode: (defaultShipper && defaultShipper.address.zipCode) || '',
      city: (defaultShipper && defaultShipper.address.city) || '',
      street: (defaultShipper && defaultShipper.address.street) || '',
      houseNumber: (defaultShipper && defaultShipper.address.number) || '',
      houseNumberSuffix: (defaultShipper && defaultShipper.address.addition) || '',
      suburb: '',
      additionalAddressLine: (defaultShipper && defaultShipper.address.additionalAddressLine) || '',
    },
    email: (defaultShipper && defaultShipper.email) || '',
    phoneNumber: (defaultShipper && defaultShipper.phoneNumber) || '',
    vatNumber: vatNumber || '',
  }
}

export const fetchProfile = async (): Promise<{
  firstName: string
  lastName: string
  address: {
    houseNumberSuffix: string
    additionalAddressLine: string[] | string
    city: string
    countryCode: string
    street: string
    postalCode: string
    houseNumber: string
    suburb: string
  }
  phoneNumber: string
  companyName: string
  id: string | null
  email: string
  vatNumber: string
}> => {
  const { shipperAddressType } = getConfig()

  switch (shipperAddressType) {
    case 'portalOrPwa':
      return fetchPortalShipperAddress().then((address) => (address.isDefined ? address : fetchPwaShipperAddress()))
    case 'pwa':
      return fetchPwaShipperAddress()
    case 'portal':
    default:
      return fetchPortalShipperAddress()
  }
}

export const saveProfile = async (profile: Profile) => {
  const tag = profile.vatNumber
    ? ['DEFAULT_SHIPPER', 'VAT-'.concat(profile.vatNumber.toUpperCase())]
    : ['DEFAULT_SHIPPER']
  const address: AddressBookEntry = {
    id: profile.id || '',
    tags: tag,
    email: profile.email,
    phoneNumber: profile.phoneNumber,
    name: {
      firstName: profile.firstName,
      lastName: profile.lastName,
      companyName: profile.companyName,
      additionalName: null,
    },
    address: {
      zipCode: profile.address.postalCode,
      city: profile.address.city,
      street: profile.address.street,
      number: profile.address.houseNumber,
      addition: profile.address.houseNumberSuffix,
      countryCode: getCountryCodeByCurrentDomain(),
      additionalInfo: null,
      additionalAddressLine: null,
    },
  }
  if (address.id) {
    await updateAddressBookEntry(address)
  } else {
    await createAddressBookEntry(address)
  }
}
